import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';

import { V1_AuthService } from '@app/api/v1/auth/v1-auth.service';
import { FetchInterceptor, FetchRequest } from '@app/shared/models/fetch-interceptor';
import { ToastHandlerService } from '../../shared/services/toast-handler.service';
import { logoutActions } from '../store/actions/logout.actions';
import { refreshTokenActions } from '../store/actions/refresh-token.actions';
import { selectAccessToken, selectRefreshToken } from '../store/selectors/token.selectors';

@Injectable()
export class FetchAuthInterceptor implements FetchInterceptor {
  private tokenRefreshed$: Observable<unknown> | null = null;

  constructor(
    private store: Store,
    private v1_AuthService: V1_AuthService,
    private toastService: ToastHandlerService,
  ) {}

  public async intercept(request: FetchRequest, next: (req: FetchRequest) => Promise<Response>): Promise<Response> {
    try {
      const token = await firstValueFrom(this.store.select(selectAccessToken));
      const response = await this.handleRequest(request, next, token);

      if (!response.ok && response.status === 401) {
        const error = await response.json().catch(() => ({}));

        if (error?.hash_expired) {
          this.store.dispatch(logoutActions.logoutWithStandardRedirect());
          throw new Error('Hash expired');
        }

        // try to refresh the token
        return this.handle401Error(request, next);
      }

      if (response instanceof Response && response.status === 422) {
        const body = await response.json();
        this.toastService.showErrorToast(body.detail[0]?.msg);
      }

      if ([401, 403, 404, 503].includes(response.status)) {
        const body = await response
          .clone()
          .json()
          .catch(() => ({ detail: response.statusText }));
        this.toastService.showErrorToast(`${response.status} - ${body.detail}`);
      }

      return response;
    } catch (error) {
      if (error instanceof Response && error.status === 401) {
        return this.handle401Error(request, next);
      } else {
        this.toastService.showErrorToast(error.message);
      }
    }
  }

  private async handleRequest(
    request: FetchRequest,
    next: (req: FetchRequest) => Promise<Response>,
    token: string | null,
  ): Promise<Response> {
    const init = request.init || {};
    const headers = new Headers(init.headers || {});

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return next({
      ...request,
      init: {
        ...init,
        headers,
      },
    });
  }

  private async handle401Error(request: FetchRequest, next: (req: FetchRequest) => Promise<Response>): Promise<Response> {
    if (!this.tokenRefreshed$) {
      this.tokenRefreshed$ = this.store.select(selectRefreshToken).pipe(
        first(),
        switchMap(refreshToken =>
          this.v1_AuthService.refreshToken({ refresh_token: refreshToken ?? '' }).pipe(
            tap({
              next: response => this.store.dispatch(refreshTokenActions.success({ response: response.data })),
              error: () => this.store.dispatch(refreshTokenActions.error()),
            }),
          ),
        ),
      );
    }

    try {
      await firstValueFrom(this.tokenRefreshed$);
      this.tokenRefreshed$ = null;
      return this.intercept(request, next);
    } catch (error) {
      this.tokenRefreshed$ = null;
      this.store.dispatch(logoutActions.logoutWithStandardRedirect());
      throw error;
    }
  }
}
